import { Fragment, ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronRight as ChevronRightIcon, OpenInNew as LinkIcon } from '@mui/icons-material';
import { Box, Button, Collapse, Container, Link, Stack, TextField, Typography } from '@mui/material';
import { ACCEPT_INVITATION_TEST_IDS } from '@localstack/constants';
import { DecorativeDivider, LogoRocket, SignUpInLegalFineprint } from '@localstack/ui';
import { InvitationService, useApiEffect, useApiGetter, VALIDATION_RULES } from '@localstack/services';

import {
  DocumentType,
  InvitationCoordinationAccountState,
  InvitationCoordinationIdp,
  InvitationCoordinationResult,
  InvitationEligibilityState,
} from '@localstack/types';

import { clearAuthStorage } from '~/util/storage';
import { BaseLayout } from '~/layouts';

import { AppRoute } from '~/config';

const COORDINATION_RESULT_DEFAULT_VALUE: InvitationCoordinationResult = {
  org_name: '',
  account_state: InvitationCoordinationAccountState.NEW_ACCOUNT,
  all_idps: [],
  strict_sso_idps: [],
  eligibility_state: InvitationEligibilityState.ELIGIBLE,
};

enum Step {
  INITIALIZING,
  ACCEPT_INVITATION_FOR_EXISTING_ACCOUNT,
  ACCEPT_INVITATION_FOR_NEW_ACCOUNT,
  CONTINUE_VIA_STRICT_SSO,
  CONTINUE_VIA_ANY_METHOD,
}

/**
 * The function determines which step to show in the acceptance flow.
 * There are two main branches with the following logic:
 * 1. Existing account
 *   1.1. First, let the user accept the invite to switch the workspace (otherwise the takeover policy is going to kick in)
 *   1.2. Next, if the org has a Strict SSO Mode ON - the game is over, the user must use a strict SSO IdP to proceed
 *   1.3. Otherwise, we let the user choose what to do next - authenticate with any method
 * 2. New account
 *   2.1. Again, if org has a Strict SSO Mode ON - the user must use a strict SSO IdP to proceed
 *   2.2. Otherwise, the user must sign-up (provide a password) or use any of the available IdPs
 *   2.3. Finally, the user can choose how to authenticate after accepting the invitation
 *
 * @param coordinationResult - The invitation coordination result
 * @param invitationAccepted - Whether the invitation has been processed
 */
const determineStep = (coordinationResult: InvitationCoordinationResult, invitationAccepted: boolean = false): Step => {
  const hasAccount = coordinationResult.account_state === InvitationCoordinationAccountState.EXISTING_ACCOUNT;
  const hasStrictSSO = coordinationResult.strict_sso_idps.length > 0;

  // coordination of existing accounts

  if (hasAccount) {
    if (invitationAccepted && hasStrictSSO) {
      return Step.CONTINUE_VIA_STRICT_SSO;
    } else if (invitationAccepted && !hasStrictSSO) {
      return Step.CONTINUE_VIA_ANY_METHOD;
    }
    return Step.ACCEPT_INVITATION_FOR_EXISTING_ACCOUNT;
  }

  // coordination of new accounts

  if (hasStrictSSO) {
    return Step.CONTINUE_VIA_STRICT_SSO;
  }
  if (!invitationAccepted) {
    return Step.ACCEPT_INVITATION_FOR_NEW_ACCOUNT;
  }
  return Step.CONTINUE_VIA_ANY_METHOD;
};

interface InvitationStackProps {
  testId: string;
  children: ReactNode;
}

const InvitationStack = ({ testId, children }: InvitationStackProps) => (
  <Stack direction="column" spacing={1} alignItems="flex-start" data-testId={testId}>
    <LogoRocket size="medium" />
    <Box mt={4} />
    {children}
  </Stack>
);

interface IdentityProviderListProps {
  idps: InvitationCoordinationIdp[];
}

/**
 * Primary call-to-action for the workspace identity providers. The label is the bare provider
 * name so the element stays addressable as `getByRole('link', { name: <idp> })`.
 */
const IdentityProviderButtons = ({ idps }: IdentityProviderListProps) => (
  <Stack direction="column" spacing={1} alignItems="flex-start">
    {idps.map((idp) => (
      <Button key={idp.name} variant="contained" color="primary" href={idp.signup_portal_url} startIcon={<LinkIcon />}>
        {idp.name}
      </Button>
    ))}
  </Stack>
);

export const AcceptInvitation = (): JSX.Element => {
  const [step, setStep] = useState<Step>(Step.INITIALIZING);
  const [password, setPassword] = useState<string | null>(null);
  const [invitationAccepted, setInvitationAccepted] = useState<boolean>(false);
  const [passwordSignUpExpanded, setPasswordSignUpExpanded] = useState<boolean>(false);

  const { token } = useParams<'token'>();

  const {
    data: coordinationResult,
    isLoading: isInvitationCoordinating,
    error: coordinationError,
  } = useApiGetter(InvitationService, 'coordinateInvitation', [token], {
    defaultValue: COORDINATION_RESULT_DEFAULT_VALUE,
    suppressErrors: true,
  });

  const { acceptInvitation, isLoading: acceptingInvitation } = useApiEffect(InvitationService, ['acceptInvitation']);

  // prevent residual contamination with unwanted auth details, which may confuse a user or some of the steps
  // (for examole, when accepting an invitation for one account while being authenticated in a different one)
  useEffect(() => {
    clearAuthStorage();
  }, []);

  // update the current step shown to the user, whenever invitation state finishes
  // loading without errors, or if our account creation state has changed
  useEffect(() => {
    if (!isInvitationCoordinating && !coordinationError) {
      setStep(determineStep(coordinationResult, invitationAccepted));
    }
  }, [isInvitationCoordinating, coordinationError, invitationAccepted]);

  const handleAcceptNewAccountInvitation = async () => {
    if (!password) {
      return;
    }

    await acceptInvitation(token, { password });
    setInvitationAccepted(true);
  };

  const handleAcceptExistingAccountInvitation = async () => {
    await acceptInvitation(token);
    setInvitationAccepted(true);
  };

  // workspaces with providers lead with them and demote the password; strict SSO workspaces
  // never reach these steps, they are routed to CONTINUE_VIA_STRICT_SSO instead
  const hasIdentityProviders = coordinationResult.all_idps.length > 0;

  const passwordSignUp = (
    <Stack direction="row" spacing={1} width="100%" alignItems="flex-start">
      <TextField
        fullWidth
        sx={{ flexGrow: 1 }}
        size="small"
        variant="outlined"
        label="Password"
        type="password"
        inputProps={{ 'data-testId': ACCEPT_INVITATION_TEST_IDS.NEW_ACCOUNT_PASSWORD_FIELD }}
        error={!!password && VALIDATION_RULES.password.validate(password) !== true}
        helperText={
          !!password && VALIDATION_RULES.password.validate(password) !== true
            ? (VALIDATION_RULES.password.validate(password) as string)
            : undefined
        }
        value={password}
        onChange={(event) => setPassword(event?.target.value)}
      />
      <Button
        variant={hasIdentityProviders ? 'outlined' : 'contained'}
        color="primary"
        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        data-testId={ACCEPT_INVITATION_TEST_IDS.NEW_ACCOUNT_CONFIRM_BUTTON}
        disabled={!password || VALIDATION_RULES.password.validate(password) !== true || acceptingInvitation}
        onClick={handleAcceptNewAccountInvitation}
      >
        Create Account & Accept Invitation
      </Button>
    </Stack>
  );

  if (coordinationError) {
    return (
      <BaseLayout hideNavigation>
        <Container
          maxWidth="sm"
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
        >
          <InvitationStack testId="error">
            <Typography variant="h4" fontWeight={600}>
              This invitation link is invalid or has expired
            </Typography>
            <Typography variant="subtitle1" color="text.tertiary">
              This invitation link is no longer valid. Please ask your organization administrator to send you a new
              invitation.
            </Typography>
          </InvitationStack>
        </Container>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout hideNavigation>
      <Container
        maxWidth="sm"
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
      >
        {step === Step.INITIALIZING && (
          <InvitationStack testId="invitation">
            <Typography variant="h4" fontWeight={600}>
              Loading your invitation details, please wait...
            </Typography>
          </InvitationStack>
        )}
        {step === Step.ACCEPT_INVITATION_FOR_EXISTING_ACCOUNT && (
          <InvitationStack testId={ACCEPT_INVITATION_TEST_IDS.ACCEPT_INVITATION_FOR_EXISTING_ACCOUNT_STEP}>
            <Typography variant="h4" fontWeight={600}>
              You&#39;ve been invited to join {coordinationResult.org_name}
            </Typography>
            <Typography variant="subtitle1" color="text.tertiary">
              If you accept, your primary workspace will switch to {coordinationResult.org_name} and your account may be
              managed by them. They won&#39;t have access to any Authentication Tokens, subscriptions, or other
              resources from your other workspaces.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              disabled={acceptingInvitation}
              onClick={handleAcceptExistingAccountInvitation}
              data-testId={ACCEPT_INVITATION_TEST_IDS.EXISTING_ACCOUNT_CONFIRM_BUTTON}
            >
              Accept Invitation
            </Button>
          </InvitationStack>
        )}
        {step === Step.ACCEPT_INVITATION_FOR_NEW_ACCOUNT && (
          <InvitationStack testId={ACCEPT_INVITATION_TEST_IDS.ACCEPT_INVITATION_FOR_NEW_ACCOUNT_STEP}>
            <Typography variant="h4" fontWeight={600}>
              You&#39;ve been invited to join {coordinationResult.org_name}
            </Typography>
            {hasIdentityProviders ? (
              <Fragment>
                <Typography variant="subtitle1" color="text.tertiary">
                  You&#39;ve been invited to join the {coordinationResult.org_name} workspace on LocalStack. Continue
                  with an identity provider below to get started.
                </Typography>
                <IdentityProviderButtons idps={coordinationResult.all_idps} />
                <DecorativeDivider />
                <Button
                  variant="text"
                  size="small"
                  aria-expanded={passwordSignUpExpanded}
                  data-testId={ACCEPT_INVITATION_TEST_IDS.NEW_ACCOUNT_PASSWORD_TOGGLE}
                  onClick={() => setPasswordSignUpExpanded((expanded) => !expanded)}
                  endIcon={<ChevronRightIcon sx={{ transform: passwordSignUpExpanded ? 'rotate(90deg)' : 'none' }} />}
                  sx={{
                    color: 'text.tertiary',
                    textTransform: 'none',
                    px: 0.5,
                    ml: -0.5,
                    '& .MuiButton-endIcon': { ml: 0.5 },
                  }}
                >
                  Alternatively, create an account with email
                </Button>
                <Collapse in={passwordSignUpExpanded} sx={{ width: '100%' }} unmountOnExit>
                  <Stack direction="column" spacing={1} pt={1}>
                    {passwordSignUp}
                    <SignUpInLegalFineprint legalDocument={DocumentType.MSA} />
                  </Stack>
                </Collapse>
              </Fragment>
            ) : (
              <Fragment>
                <Typography variant="subtitle1" color="text.tertiary">
                  You&#39;ve been invited to join the {coordinationResult.org_name} workspace on LocalStack. A new
                  account will be created for you automatically — set a strong password below to get started.
                </Typography>
                {passwordSignUp}
                <Box mt={1} width="100%">
                  <SignUpInLegalFineprint legalDocument={DocumentType.MSA} />
                </Box>
              </Fragment>
            )}
          </InvitationStack>
        )}
        {step === Step.CONTINUE_VIA_STRICT_SSO && (
          <InvitationStack testId={ACCEPT_INVITATION_TEST_IDS.STRICT_SSO_STEP}>
            <Typography variant="h4" fontWeight={600}>
              Sign in via SSO to join {coordinationResult.org_name}
            </Typography>
            <Typography variant="subtitle1" color="text.tertiary">
              The {coordinationResult.org_name} workspace enforces SSO authentication. You must sign in using one of the
              authorized identity providers listed below.
            </Typography>
            <Typography variant="subtitle1" color="warning.main">
              Authentication is handled entirely by the SSO provider configured by {coordinationResult.org_name}. Make
              sure you trust this organization before continuing.
            </Typography>
            <IdentityProviderButtons idps={coordinationResult.strict_sso_idps} />
          </InvitationStack>
        )}
        {step === Step.CONTINUE_VIA_ANY_METHOD && (
          <InvitationStack testId={ACCEPT_INVITATION_TEST_IDS.FINALIZATION_STEP}>
            <Typography variant="h4" fontWeight={600}>
              You&#39;re all set — sign in to continue
            </Typography>
            {hasIdentityProviders ? (
              <Fragment>
                <Typography variant="subtitle1" color="text.tertiary">
                  Your invitation has been accepted. Continue with an identity provider below:
                </Typography>
                <IdentityProviderButtons idps={coordinationResult.all_idps} />
                <DecorativeDivider />
                <Typography variant="subtitle1" color="text.tertiary">
                  Or head to the <Link href={AppRoute.SIGN_IN}>sign-in</Link> page to access your account with a
                  password.
                </Typography>
              </Fragment>
            ) : (
              <Typography variant="subtitle1" color="text.tertiary">
                Your invitation has been accepted. Head to the <Link href={AppRoute.SIGN_IN}>sign-in</Link> page to
                access your account.
              </Typography>
            )}
          </InvitationStack>
        )}
      </Container>
    </BaseLayout>
  );
};
